/**
 * Canonical RBAC permission ids from the `listPermissions` catalog.
 * Keep in sync with `CUSTOM_ROLE_PERMISSION_GROUPS` below.
 */
export const RBAC_PERMISSIONS = {
  READ_ASSETS: 'assets:read',
  WRITE_ASSETS: 'assets:write',
  DOWNLOAD_ASSETS: 'assets:download',
  EXPORT_SBOM: 'assets:ExportSBOM',
  SECRETS_VIEW: 'assets:secrets_view',
  READ_ASSET_GROUPS: 'asset_groups:read',
  WRITE_ASSET_GROUPS: 'asset_groups:write',
  FINDINGS_SBOM_EDIT: 'findings:SBOMEdit',
  FINDINGS_REMEDIATE: 'findings:remediate',
  FINDINGS_EXPORT: 'findings:export',
  RISEAI_USE: 'riseai:use',
  JIRA_TICKETS_VIEW: 'jira:tickets_view',
  JIRA_TICKETS_CREATE: 'jira:tickets_create',
  AUDIT_READ: 'audit:read',
  SETTINGS_GENERAL_VIEW: 'settings:general_view',
  SETTINGS_GENERAL_MANAGE: 'settings:general_manage',
  SETTINGS_ADMINISTRATION_VIEW: 'settings:administration_view',
  SETTINGS_ADMINISTRATION_MANAGE: 'settings:administration_manage',
  SETTINGS_NOTIFICATIONS_VIEW: 'settings:notifications_view',
  SETTINGS_NOTIFICATIONS_MANAGE: 'settings:notifications_manage',
  SETTINGS_INTEGRATIONS_VIEW: 'settings:integrations_view',
  SETTINGS_INTEGRATIONS_MANAGE: 'settings:integrations_manage',
  SETTINGS_SSO_VIEW: 'settings:sso_view',
  SETTINGS_SSO_MANAGE: 'settings:sso_manage'
};

/** Maps to `CONSTANTS.PERMISSIONS.RBAC_*` keys used across the app. */
export const RBAC_PERMISSION_CONSTANTS = {
  RBAC_READ_ASSETS: RBAC_PERMISSIONS.READ_ASSETS,
  RBAC_WRITE_ASSETS: RBAC_PERMISSIONS.WRITE_ASSETS,
  RBAC_DOWNLOAD_ASSETS: RBAC_PERMISSIONS.DOWNLOAD_ASSETS,
  RBAC_EXPORT_SBOM: RBAC_PERMISSIONS.EXPORT_SBOM,
  RBAC_SECRETS_VIEW: RBAC_PERMISSIONS.SECRETS_VIEW,
  RBAC_VIEW_ASSET_GROUP: RBAC_PERMISSIONS.READ_ASSET_GROUPS,
  RBAC_WRITE_ASSET_GROUP: RBAC_PERMISSIONS.WRITE_ASSET_GROUPS,
  RBAC_FINDINGS_SBOM_EDIT: RBAC_PERMISSIONS.FINDINGS_SBOM_EDIT,
  RBAC_FINDINGS_REMEDIATE: RBAC_PERMISSIONS.FINDINGS_REMEDIATE,
  RBAC_FINDINGS_EXPORT: RBAC_PERMISSIONS.FINDINGS_EXPORT,
  RBAC_RISEAI_USE: RBAC_PERMISSIONS.RISEAI_USE,
  RBAC_JIRA_TICKETS_VIEW: RBAC_PERMISSIONS.JIRA_TICKETS_VIEW,
  RBAC_JIRA_TICKETS_CREATE: RBAC_PERMISSIONS.JIRA_TICKETS_CREATE,
  RBAC_AUDIT_READ: RBAC_PERMISSIONS.AUDIT_READ,
  RBAC_VIEW_GENERAL: RBAC_PERMISSIONS.SETTINGS_GENERAL_VIEW,
  RBAC_MANAGE_GENERAL: RBAC_PERMISSIONS.SETTINGS_GENERAL_MANAGE,
  RBAC_VIEW_USERS_ACCESS: RBAC_PERMISSIONS.SETTINGS_ADMINISTRATION_VIEW,
  RBAC_MANAGE_USERS_ACCESS: RBAC_PERMISSIONS.SETTINGS_ADMINISTRATION_MANAGE,
  RBAC_VIEW_NOTIFICATIONS: RBAC_PERMISSIONS.SETTINGS_NOTIFICATIONS_VIEW,
  RBAC_MANAGE_NOTIFICATIONS: RBAC_PERMISSIONS.SETTINGS_NOTIFICATIONS_MANAGE,
  RBAC_VIEW_INTEGRATIONS: RBAC_PERMISSIONS.SETTINGS_INTEGRATIONS_VIEW,
  RBAC_MANAGE_INTEGRATIONS: RBAC_PERMISSIONS.SETTINGS_INTEGRATIONS_MANAGE,
  RBAC_VIEW_SSO: RBAC_PERMISSIONS.SETTINGS_SSO_VIEW,
  RBAC_MANAGE_SSO: RBAC_PERMISSIONS.SETTINGS_SSO_MANAGE
};

const P = RBAC_PERMISSIONS;

/**
 * FE layout for the permission picker (grouping, labels, required, requires).
 * Never render this directly in the UI — always merge with `listPermissions` via
 * `buildPermissionGroupsFromCatalog` so the catalog controls visibility and descriptions.
 */
export const CUSTOM_ROLE_PERMISSION_GROUPS = [
  {
    id: 'assets',
    label: 'Assets',
    permissions: [
      {
        label: 'View',
        value: P.READ_ASSETS,
        required: true,
        description: 'View assets, files, and findings (vulnerabilities, licenses, misconfigs, PQC, and redacted secrets)'
      },
      {
        label: 'Manage',
        value: P.WRITE_ASSETS,
        description: 'Upload, modify, and edit assets'
      },
      {
        label: 'Download',
        value: P.DOWNLOAD_ASSETS,
        description: 'Download assets and files'
      },
      {
        label: 'Export SBOM',
        value: P.EXPORT_SBOM,
        description: 'Download SBOM'
      },
      {
        label: 'Reveal Secrets',
        value: P.SECRETS_VIEW,
        description: 'View raw, unredacted secret material'
      }
    ]
  },
  {
    id: 'asset_groups',
    label: 'Asset Groups',
    permissions: [
      {
        label: 'View',
        value: P.READ_ASSET_GROUPS,
        required: true,
        description: 'View asset groups and asset-to-group memberships'
      },
      {
        label: 'Manage',
        value: P.WRITE_ASSET_GROUPS,
        description: 'Create, manage, and assign assets to asset groups'
      }
    ]
  },
  {
    id: 'asset_actions',
    label: 'Asset Actions',
    permissions: [
      {
        label: 'SBOM Edit',
        value: P.FINDINGS_SBOM_EDIT,
        description: 'Edit SBOM contents'
      },
      {
        label: 'Remediations',
        value: P.FINDINGS_REMEDIATE,
        description: 'Apply remediation actions (RiseAI, secrets, vulnerabilities, etc.)'
      },
      {
        label: 'Generate PDF Reports',
        value: P.FINDINGS_EXPORT,
        description: 'Generate PDF reports'
      }
    ]
  },
  {
    id: 'riseai',
    label: 'Rise AI',
    permissions: [
      {
        label: 'Chatbot',
        value: P.RISEAI_USE,
        description: 'Use the RiseAI chatbot to query and analyze'
      }
    ]
  },
  {
    id: 'jira',
    label: 'Jira',
    permissions: [
      {
        label: 'View Tickets',
        value: P.JIRA_TICKETS_VIEW,
        description: 'View Jira tickets linked to findings'
      },
      {
        label: 'Create Tickets',
        value: P.JIRA_TICKETS_CREATE,
        requires: P.JIRA_TICKETS_VIEW,
        description: 'Create Jira tickets from findings'
      }
    ]
  },
  {
    id: 'audit',
    label: 'Audit',
    permissions: [
      {
        label: 'View',
        value: P.AUDIT_READ,
        description: 'View audit logs'
      }
    ]
  },
  {
    id: 'settings',
    label: 'Settings',
    subGroups: [
      {
        id: 'general',
        label: 'General',
        permissions: [
          {
            label: 'View',
            value: P.SETTINGS_GENERAL_VIEW,
            description: 'View general org settings'
          },
          {
            label: 'Manage',
            value: P.SETTINGS_GENERAL_MANAGE,
            requires: P.SETTINGS_GENERAL_VIEW,
            description: 'Modify general org settings (session timeout, AI toggles, advanced settings)'
          }
        ]
      },
      {
        id: 'users_access',
        label: 'Users & Access',
        permissions: [
          {
            label: 'View',
            value: P.SETTINGS_ADMINISTRATION_VIEW,
            description: 'View users, security groups, roles, and access control'
          },
          {
            label: 'Manage',
            value: P.SETTINGS_ADMINISTRATION_MANAGE,
            requires: P.SETTINGS_ADMINISTRATION_VIEW,
            description: 'Manage users, security groups, roles, and access control'
          }
        ]
      },
      {
        id: 'notifications',
        label: 'Notifications',
        permissions: [
          {
            label: 'View',
            value: P.SETTINGS_NOTIFICATIONS_VIEW,
            description: 'View webhooks, email, and in-app notification configurations'
          },
          {
            label: 'Manage',
            value: P.SETTINGS_NOTIFICATIONS_MANAGE,
            requires: P.SETTINGS_NOTIFICATIONS_VIEW,
            description: 'Manage webhooks, email, and in-app notification configurations'
          }
        ]
      },
      {
        id: 'integrations',
        label: 'Integrations',
        permissions: [
          {
            label: 'View',
            value: P.SETTINGS_INTEGRATIONS_VIEW,
            description: 'View platform integrations'
          },
          {
            label: 'Manage',
            value: P.SETTINGS_INTEGRATIONS_MANAGE,
            requires: P.SETTINGS_INTEGRATIONS_VIEW,
            description: 'Add, edit, and remove platform integrations'
          }
        ]
      },
      {
        id: 'sso',
        label: 'SSO',
        permissions: [
          {
            label: 'View',
            value: P.SETTINGS_SSO_VIEW,
            description: 'View SAML SSO, SCIM provisioning, and IDP group mappings'
          },
          {
            label: 'Manage',
            value: P.SETTINGS_SSO_MANAGE,
            requires: P.SETTINGS_SSO_VIEW,
            description: 'Configure SAML SSO, SCIM provisioning, and IDP group mappings'
          }
        ]
      }
    ]
  }
];

export function getGroupPermissions(group) {
  if (group.subGroups?.length) {
    return group.subGroups.flatMap(subGroup => subGroup.permissions ?? []);
  }

  return group.permissions ?? [];
}

export function flattenPermissionGroups(groups = CUSTOM_ROLE_PERMISSION_GROUPS) {
  return groups.flatMap(group => getGroupPermissions(group));
}

function derivePermissionLabel(permissionId) {
  const action = permissionId.split(':')[1] ?? permissionId;
  const labels = { read: 'View', write: 'Manage', use: 'Use' };
  return labels[action] ?? action.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function mergeLayoutPermission(layoutPermission, catalogById) {
  const catalogPermission = catalogById.get(layoutPermission.value);
  if (!catalogPermission) return null;

  return {
    label: layoutPermission.label,
    value: layoutPermission.value,
    description: catalogPermission.description || layoutPermission.description || '',
    ...(layoutPermission.required && { required: true }),
    ...(layoutPermission.requires && { requires: layoutPermission.requires })
  };
}

function buildGroupFromLayout(groupLayout, catalogById) {
  if (groupLayout.subGroups?.length) {
    const subGroups = groupLayout.subGroups
      .map(subGroup => ({
        id: subGroup.id,
        label: subGroup.label,
        permissions: (subGroup.permissions ?? []).map(permission => mergeLayoutPermission(permission, catalogById)).filter(Boolean)
      }))
      .filter(subGroup => subGroup.permissions.length);

    if (!subGroups.length) return null;

    return { id: groupLayout.id, label: groupLayout.label, subGroups };
  }

  const permissions = (groupLayout.permissions ?? []).map(permission => mergeLayoutPermission(permission, catalogById)).filter(Boolean);
  if (!permissions.length) return null;

  return { id: groupLayout.id, label: groupLayout.label, permissions };
}

/**
 * Merges `CUSTOM_ROLE_PERMISSION_GROUPS` with the RBAC catalog from `listPermissions`.
 * Layout controls grouping, labels, required flags, and dependencies; descriptions
 * come from the BE. Permissions removed from the catalog are omitted. New catalog
 * permissions not in the layout are appended to an "Other" group.
 */
export function buildPermissionGroupsFromCatalog(catalogPermissions = [], layout = CUSTOM_ROLE_PERMISSION_GROUPS) {
  const catalogById = new Map(catalogPermissions.map(permission => [permission.id, permission]));
  const groups = layout.map(groupLayout => buildGroupFromLayout(groupLayout, catalogById)).filter(Boolean);

  const layoutIds = new Set(flattenPermissionGroups(layout).map(permission => permission.value));
  const ungrouped = catalogPermissions.filter(permission => !layoutIds.has(permission.id));

  if (ungrouped.length) {
    groups.push({
      id: 'other',
      label: 'Other',
      permissions: ungrouped.map(permission => ({
        label: derivePermissionLabel(permission.id),
        value: permission.id,
        description: permission.description ?? ''
      }))
    });
  }

  return groups;
}

export function getPermissionLabelLookup(permissionGroups) {
  return flattenPermissionGroups(permissionGroups).reduce((lookup, permission) => {
    lookup[permission.value] = permission.label;
    return lookup;
  }, {});
}

export function parseRolePermissionsFromGroups(permissionIds = [], permissionGroups = []) {
  const permissionSet = new Set(permissionIds);
  const labelByValue = getPermissionLabelLookup(permissionGroups);

  const groups = permissionGroups.flatMap(group => {
    if (group.subGroups?.length) {
      return group.subGroups
        .map(subGroup => ({
          id: subGroup.id,
          label: subGroup.label,
          permissions: subGroup.permissions.filter(permission => permissionSet.has(permission.value))
        }))
        .filter(subGroup => subGroup.permissions.length > 0);
    }

    const permissions = getGroupPermissions(group).filter(permission => permissionSet.has(permission.value));
    if (!permissions.length) return [];

    return [{ id: group.id, label: group.label, permissions }];
  });

  const permissionCount = groups.reduce((count, group) => count + group.permissions.length, 0);

  return {
    groups,
    permissionCount,
    unknownPermissions: permissionIds.filter(permissionId => !labelByValue[permissionId])
  };
}

export function filterToKnownPermissions(permissions = [], groups = CUSTOM_ROLE_PERMISSION_GROUPS) {
  const known = new Set(flattenPermissionGroups(groups).map(permission => permission.value));
  return permissions.filter(permission => known.has(permission));
}

export const REQUIRED_CUSTOM_ROLE_PERMISSIONS = flattenPermissionGroups()
  .filter(permission => permission.required)
  .map(permission => permission.value);

export function buildPermissionDependencyMaps(groups = CUSTOM_ROLE_PERMISSION_GROUPS) {
  const flatPermissions = flattenPermissionGroups(groups);

  const permissionRequires = flatPermissions.reduce((map, permission) => {
    if (permission.requires) {
      map[permission.value] = permission.requires;
    }
    return map;
  }, {});

  const requiredBy = Object.entries(permissionRequires).reduce((map, [dependent, dependency]) => {
    (map[dependency] = map[dependency] ?? []).push(dependent);
    return map;
  }, {});

  const requiredPermissions = flatPermissions.filter(permission => permission.required).map(permission => permission.value);

  return { permissionRequires, requiredBy, requiredPermissions };
}

export function ensureRequiredPermissions(permissions = [], groups = CUSTOM_ROLE_PERMISSION_GROUPS) {
  const { requiredPermissions } = buildPermissionDependencyMaps(groups);
  return [...new Set([...requiredPermissions, ...permissions])];
}

export function applyPermissionDependencies(permissions = [], change, groups = CUSTOM_ROLE_PERMISSION_GROUPS) {
  const { permissionRequires, requiredBy } = buildPermissionDependencyMaps(groups);
  const nextSet = new Set(permissions);

  if (change) {
    const { value, checked } = change;

    if (checked) {
      nextSet.add(value);

      const dependency = permissionRequires[value];
      if (dependency) {
        nextSet.add(dependency);
      }
    } else {
      nextSet.delete(value);

      (requiredBy[value] ?? []).forEach(dependent => nextSet.delete(dependent));
    }
  }

  Object.entries(permissionRequires).forEach(([dependent, dependency]) => {
    if (nextSet.has(dependent)) {
      nextSet.add(dependency);
    }
  });

  return ensureRequiredPermissions([...nextSet], groups).sort();
}

export function normalizeCustomRolePermissions(permissions = [], groups = CUSTOM_ROLE_PERMISSION_GROUPS) {
  const knownPermissions = filterToKnownPermissions(permissions, groups);
  return applyPermissionDependencies(ensureRequiredPermissions(knownPermissions, groups), undefined, groups);
}
